import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib'

const badgeVariants = cva(
  'border-transparent inline-flex items-center justify-center rounded-full border px-4 py-0.5 text-sm font-semibold font-nunito w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 focus-visible:ring-4 focus-visible:outline-1 aria-invalid:focus-visible:ring-0 transition-[color,box-shadow]',
  {
    variants: {
      variant: {
        default: 'bg-neutral-100 text-neutral-800 shadow-sm [a&]:hover:bg-primary/90',
        primary: 'bg-sky-100 text-primary shadow-sm [a&]:hover:bg-primary/90',
        success: 'bg-green-100 text-green-800 shadow-sm [a&]:hover:bg-primary/90',
        destructive: 'bg-red-100 text-red-800 shadow-sm [a&]:hover:bg-primary/90',
        warning: 'bg-orange-100 text-orange-800 [a&]:hover:bg-secondary/90',
        'warning-dark': 'bg-orange-200 text-orange-600 shadow-sm [a&]:hover:bg-primary/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function Badge({
  className,
  variant,
  asChild = false,
  backgroundColor,
  textColor,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean; backgroundColor?: string; textColor?: string }) {
  const Comp = asChild ? Slot : 'span'

  const style = {
    backgroundColor: backgroundColor,
    color: textColor,
  }

  return (
    <Comp
      data-slot="badge"
      data-testid={`badge-${variant}`}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
      style={style}
    />
  )
}

export { Badge, badgeVariants }
